import type { TBookingContext, TCartGroup } from '@providers/booking/types'
import { getBaseGroup } from 'components-ui/src/offer_card/helper'
import {
  BookingSearchByDaysClientDto,
  BookingSearchClientDto,
  APILocationBookingConfClientDtoResponse,
} from 'service-api/src/types/api'
import { ClientBookingSearchConfigsService } from 'service-api/src/services/client_booking_search_configs.service'
import { ClientLocationBookingConfService } from 'service-api/src/services/client_location_booking_conf.service'

export const sortOffers = (
  offers: BookingSearchClientDto[] | BookingSearchByDaysClientDto[],
  serviceTypeName: string,
  petGroup?: number
): BookingSearchClientDto[] | BookingSearchByDaysClientDto[] => {
  const sortedOffers = offers.sort((a, b) => {
    const firstAvailabilityGroups =
      (a as BookingSearchClientDto).availabilityGroups ||
      (a as BookingSearchByDaysClientDto).availabilityGroupsByDays ||
      []
    const secondAvailabilityGroups =
      (b as BookingSearchClientDto).availabilityGroups ||
      (b as BookingSearchByDaysClientDto).availabilityGroupsByDays ||
      []
    const firstAvailability = getBaseGroup(serviceTypeName, petGroup || 0, firstAvailabilityGroups).availabilities?.[0]
      .available
    const secondAvailability = getBaseGroup(serviceTypeName, petGroup || 0, secondAvailabilityGroups)
      .availabilities?.[0].available

    if (firstAvailability === secondAvailability) {
      return (a.offer?.order || 0) - (b.offer?.order || 0)
    } else {
      return firstAvailability ? -1 : 1
    }
  })

  return sortedOffers
}

export const hasOffersDiscount = (
  offers: BookingSearchClientDto[] | BookingSearchByDaysClientDto[],
  isOvernight: boolean = false
) =>
  !!offers.find((offer) =>
    isOvernight
      ? (offer as BookingSearchClientDto).availabilityGroups?.find((availabilityGroup) =>
          availabilityGroup?.availabilities?.find((availability) => availability.price?.discounts?.length)
        )
      : (offer as BookingSearchByDaysClientDto).availabilityGroupsByDays?.find((availabilityGroup) =>
          availabilityGroup?.[0].availabilities?.find((availability) => availability.price?.discounts?.length)
        )
  )

export const setUpTemperTest = (
  ctx: TBookingContext,
  dispatch: ({ type, payload }: { type: string; payload?: GenericSimpleBag }) => void,
  affectedByPetIds: string[]
): void => {
  dispatch({ type: 'SET_SERVICE_TYPE', payload: { value: ctx.data.temperTest } })
  // don't check affectedByIds for new customers because they don't have petIds yet so it can't match correctly -- just include them
  if (!ctx.isNewCustomer) {
    ctx.groups.map((group, groupIdx) => {
      group.pets.map((pet, petIdx) => {
        if (!affectedByPetIds.includes(pet.id)) {
          dispatch({
            type: 'SET_PET_IN_ROOM',
            payload: { groupIdx: groupIdx, petIdx: petIdx, value: 0 },
          })
        } else {
          // we want all temper test pets in the same room so the search request is correct
          dispatch({
            type: 'SET_PET_IN_ROOM',
            payload: { groupIdx: groupIdx, petIdx: petIdx, value: 1 },
          })
        }
      })
    })
  }
  dispatch({ type: 'REGROUP_PETS' })
}

export const configureTemperTest = async ({
  temperTestId,
  dispatch,
  accountName,
}: {
  temperTestId: string
  dispatch: ({ type, payload }: { type: string; payload?: GenericSimpleBag }) => void
  accountName: string
}) => {
  const promises = []
  promises.push(
    ClientBookingSearchConfigsService.getData(accountName, 'temper-test').then((res) => {
      dispatch({ type: 'SET_BOOKING_CONFIG', payload: { serviceTypeName: 'temper-test', value: res } })
    })
  )
  promises.push(
    ClientLocationBookingConfService.getConfiguration(accountName, temperTestId || '').then(
      (res: APILocationBookingConfClientDtoResponse) => {
        dispatch({
          type: 'SET_SERVICE_TYPE_CONFIG',
          payload: { value: res.results },
        })
      }
    )
  )
  await Promise.all(promises)
}

export const listPetNames = (ctx: TBookingContext, petGroup: number): string[] | undefined => {
  const groups = ctx.cart.groups[0]?.pets?.[0]?.name ? ctx.cart.groups : ctx.confirmation.groups
  return groups?.[petGroup]?.pets.map(({ name }) => name)
}

export const isGroupSelectionCompleted = (group: TCartGroup): boolean => {
  // group must have a primary offer selected
  if (!group.offerSelected) return false

  // if no required add-ons, can continue
  if (!group.offerSelected.hasRequiredAddons) return true

  // Otherwise, all pets in the group must have required add-ons
  return group.pets.every((_, petIdx) => !!group.requiredAddOns[petIdx])
}

export const allGroupsSelectionCompleted = (groups: TCartGroup[]): boolean => {
  return groups.every((group) => isGroupSelectionCompleted(group))
}
